<script setup>
import { BarChart } from '@dashhudson/dashing-ui/bar-chart';
import { Button } from '@dashhudson/dashing-ui/button';
import { ChartCard } from '@dashhudson/dashing-ui/chart-card';
import { useDrawer } from '@dashhudson/dashing-ui/drawer';
import { HorizontalBarChart } from '@dashhudson/dashing-ui/horizontal-bar-chart';
import { MetricList } from '@dashhudson/dashing-ui/metric-list';
import { Section } from '@dashhudson/dashing-ui/section';
import { SectionHeader } from '@dashhudson/dashing-ui/section-header';
import { Select } from '@dashhudson/dashing-ui/select';
import { isEmpty } from 'lodash-es';
import { computed, ref } from 'vue';

import {
  DEMOGRAPHICS_AGE_GENDER_METRIC_OPTIONS,
  NO_DATA_FOUND,
  NO_DATA_FOUND_FOR_FILTERS,
  SOCIAL_LISTENING_DRAWERS,
  TOP_LOCATIONS_COUNT,
  TOP_LOCATIONS_METRIC_OPTIONS,
  TOPIC_COMPARISONS_TOOLTIPS,
} from '@/app/socialListening/constants';
import { useSocialListeningStore } from '@/stores/social-listening';
import { useSocialListeningComparisonsStore } from '@/stores/social-listening-comparisons';
import { useSocialListeningTopicsStore } from '@/stores/social-listening-topics';
import { getCountryFlag } from '@/utils/countries';
import { capitalize, formatNumber } from '@/utils/formatters';

const comparisonStore = useSocialListeningComparisonsStore();
const topicsStore = useSocialListeningTopicsStore();
const listeningStore = useSocialListeningStore();

const { launchDrawer } = useDrawer();

const noDataMessage = computed(() =>
  isEmpty(comparisonStore.comparisonFilters) ? NO_DATA_FOUND : NO_DATA_FOUND_FOR_FILTERS,
);

const chartOptions = computed(() => ({
  scales: {
    x: {
      stacked: true,
    },
    y: {
      grace: 20,
      max: 100,
      stacked: true,
      ticks: { callback: (value) => `${value}%` },
    },
  },
}));

// Gender
const genderIsNoData = computed(() => {
  if (isEmpty(comparisonStore.topicsPercentagePostVolumePerGender)) {
    return true;
  }

  return Object.values(comparisonStore.topicsPercentagePostVolumePerGender).every((topic) =>
    Object.values(topic).every((count) => count === 0),
  );
});

const genderChartData = computed(() => {
  const topics = Object.keys(comparisonStore.topicsPercentagePostVolumePerGender);
  if (genderIsNoData.value) {
    return { datasets: [] };
  }

  const datasets = comparisonStore.genderLabels.map((gender) => ({
    label: capitalize(gender),
    data: topics.map((topic) => {
      const topicData = comparisonStore.topicsPercentagePostVolumePerGender[topic];
      return { x: topic, y: topicData[gender] };
    }),
  }));

  return { datasets };
});
function genderGenerateTooltip({ tooltip }) {
  const topicName = tooltip.title[0];
  const topicData = comparisonStore.topicsPercentagePostVolumePerGender[topicName];

  const genderColorMap = {};
  if (tooltip.chart?.data?.datasets) {
    tooltip.chart.data.datasets.forEach((dataset) => {
      genderColorMap[dataset.label] = dataset.backgroundColor;
    });
  }

  const data = Object.entries(topicData).map(([gender, value]) => ({
    label: capitalize(gender),
    value: `${value}%`,
    dot: {
      style: {
        backgroundColor: genderColorMap[capitalize(gender)],
      },
    },
  }));

  return {
    title: topicName,
    data,
  };
}
const genderActiveTooltip = computed(
  () => (tooltipContext) => genderGenerateTooltip(tooltipContext),
);

// Age
const ageSelectedMetric = ref(DEMOGRAPHICS_AGE_GENDER_METRIC_OPTIONS.ALL);
const ageIsNoData = computed(() => {
  if (isEmpty(comparisonStore.topicsPercentagePostVolumePerAge)) return true;

  return Object.values(comparisonStore.topicsPercentagePostVolumePerAge).every((topic) =>
    Object.values(topic).every((ageGroup) => Object.values(ageGroup).every((value) => value === 0)),
  );
});

const ageChartData = computed(() => {
  const topicsData = comparisonStore.topicsPercentagePostVolumePerAge;
  const topics = Object.keys(topicsData);
  if (ageIsNoData.value) {
    return { datasets: [] };
  }

  const selectedMetric = ageSelectedMetric.value.value;

  const datasets = comparisonStore.ageLabels.map((ageLabel) => ({
    label: capitalize(ageLabel),
    data: topics.map((topic) => ({
      x: topic,
      y: topicsData[topic][ageLabel][selectedMetric] ?? 0,
    })),
  }));
  return { datasets };
});

function ageGenerateTooltip({ tooltip }) {
  const topicName = tooltip.title[0];
  const topicData = comparisonStore.topicsPercentagePostVolumePerAge[topicName];
  const selectedMetric = ageSelectedMetric.value.value;

  if (!topicData) return { title: topicName, data: [] };

  const ageColorMap = {};
  if (tooltip.chart?.data?.datasets) {
    tooltip.chart.data.datasets.forEach((dataset) => {
      ageColorMap[dataset.label] = dataset.backgroundColor;
    });
  }

  const data = Object.entries(topicData).map(([ageLabel, genderData]) => ({
    label: ageLabel,
    value: `${genderData[selectedMetric]}%`,
    dot: {
      style: {
        backgroundColor: ageColorMap[capitalize(ageLabel)],
      },
    },
  }));

  return {
    title: topicName,
    data,
  };
}
const ageActiveTooltip = computed(() => (tooltipContext) => ageGenerateTooltip(tooltipContext));

// Top Locations
const topLocationsExpanded = ref(false);
const topLocationsSelectedMetric = ref(TOP_LOCATIONS_METRIC_OPTIONS.COUNTRY);
const topLocationsLoading = computed(() => listeningStore.pending.topLocations);

function updateTopLocations(metric) {
  if (!metric || metric.value === topicsStore.topLocationsSelectedMetric) {
    return;
  }
  topLocationsSelectedMetric.value = metric;
  topicsStore.setTopLocationsSelectedMetric(metric.value);
  topicsStore.getTopLocations();
}

function addCountryFlag(location) {
  if (!location) {
    return location;
  }
  const parts = location.split(',').map((part) => part.trim());
  const country = parts.at(-1);
  const countryFlag = getCountryFlag(country);
  if (countryFlag === '-') return location;
  parts[parts.length - 1] = `${countryFlag} ${country}`;
  return parts.join(', ');
}

function handleSeeMoreTopLocations() {
  topLocationsExpanded.value = !topLocationsExpanded.value;
}

const topLocationsMeters = computed(() => {
  if (topLocationsLoading.value) {
    return [];
  }
  return listeningStore.topLocations.map((item) => ({
    label: addCountryFlag(item.location),
    location: item.location,
    data: [item.count],
  }));
});
const topLocationsSlicedMeters = computed(() => {
  const maxCount = topLocationsExpanded.value
    ? TOP_LOCATIONS_COUNT.EXPANDED
    : TOP_LOCATIONS_COUNT.COLLAPSED;
  return topLocationsMeters.value.slice(0, maxCount);
});
const topLocationsMaxValue = computed(() => {
  const values = listeningStore.topLocations.map((item) => item.count);
  if (values.length === 0) return 0;
  return Math.max(...values);
});
const topLocationsNoData = computed(() => topLocationsMeters.value.length === 0);
const seeMoreTopLocationsButtonVisible = computed(
  () =>
    !topLocationsLoading.value &&
    listeningStore.topLocations.length > TOP_LOCATIONS_COUNT.COLLAPSED,
);

const seeMoreTopLocationsButtonText = computed(() =>
  topLocationsExpanded.value ? 'Collapse' : `Expand`,
);

function buildLocationFilter(location) {
  const metric = topLocationsSelectedMetric.value.value;
  const parts = location.split(',').map((p) => p.trim());
  const mainPart = parts[0];
  const country = parts.at(-1);

  switch (metric) {
    case TOP_LOCATIONS_METRIC_OPTIONS.COUNTRY.value:
      return { city: null, administrativeDivision: null, country };

    case TOP_LOCATIONS_METRIC_OPTIONS.CITY.value:
      return { city: mainPart, administrativeDivision: null, country };

    case TOP_LOCATIONS_METRIC_OPTIONS.ADMINISTRATIVE_DIVISION.value:
      return { city: null, administrativeDivision: mainPart, country };

    default:
      return { city: null, administrativeDivision: null, country: null };
  }
}

async function handleLocationsMeterGroupClicked(group) {
  const locationFilters = buildLocationFilter(group.meter.location);

  comparisonStore.setDrawerSelectedTopic(topicsStore.selectedTopic);
  const { keywordsAndHashtags, ...comparisonTemporaryFiltersData } =
    comparisonStore.temporaryFilters;

  comparisonStore.setTempFilters({
    locations: [locationFilters],
    keywordsAndHashtags:
      comparisonStore.drawerSelectedTopic?.searchBody?.filters?.keywordsAndHashtags,
    ...comparisonTemporaryFiltersData,
  });

  await launchDrawer({
    name: SOCIAL_LISTENING_DRAWERS.TOP_POST,
    props: {
      ...comparisonStore.drawerSerializedFilters,
      keywordsAndHashtags:
        comparisonStore.drawerSelectedTopic?.searchBody?.filters?.keywordsAndHashtags,
      topicId: comparisonStore.drawerSelectedTopic?.id,
    },
  });
}
</script>

<template>
  <Section class="gap-4">
    <SectionHeader title="Demographics" />
    <div class="flex flex-row flex-wrap gap-5">
      <ChartCard
        title="Gender"
        :title-tooltip="TOPIC_COMPARISONS_TOOLTIPS.MENTIONS_BY_GENDER"
        class="!max-w-50 !flex-1"
        :skeleton="comparisonStore.pendingDataPull.topicsPostVolumePerGender"
      >
        <BarChart
          :data="genderChartData"
          :loading="comparisonStore.pendingDataPull.topicsPostVolumePerGender"
          :options="chartOptions"
          :no-data="genderIsNoData"
          :no-data-message="noDataMessage"
          :show-legend="!genderIsNoData"
        >
          <template #tooltip="genderTooltipContext">
            {{ genderActiveTooltip(genderTooltipContext).title }}
            <MetricList :metric-items="genderActiveTooltip(genderTooltipContext).data" />
          </template>
        </BarChart>
      </ChartCard>

      <ChartCard
        title="Age"
        :title-tooltip="TOPIC_COMPARISONS_TOOLTIPS.MENTIONS_BY_AGE"
        class="!max-w-50 !flex-1"
        :skeleton="comparisonStore.pendingDataPull.topicsPostVolumePerAge"
      >
        <template #filters="{ disabled }">
          <Select
            v-slot="{ selectionLabel }"
            v-model="ageSelectedMetric"
            :disabled="disabled"
            :options="Object.values(DEMOGRAPHICS_AGE_GENDER_METRIC_OPTIONS)"
            option-label="optionLabel"
            :dropdown="{ placement: 'bottom-end' }"
            :hide-controls="false"
            return-object
          >
            <Button
              :label="selectionLabel"
              :disabled="comparisonStore.isAgeBreakdownOnly"
              severity="tertiary"
              icon-right="filter-lines"
            />
          </Select>
        </template>
        <BarChart
          :data="ageChartData"
          :loading="comparisonStore.pendingDataPull.topicsPostVolumePerAge"
          :options="chartOptions"
          :no-data="ageIsNoData"
          :no-data-message="noDataMessage"
          :show-legend="!ageIsNoData"
        >
          <template #tooltip="ageTooltipContext">
            {{ ageActiveTooltip(ageTooltipContext).title }}
            <MetricList :metric-items="ageActiveTooltip(ageTooltipContext).data" />
          </template>
        </BarChart>
      </ChartCard>
    </div>
    <ChartCard
      title="Top Locations"
      :title-tooltip="TOPIC_COMPARISONS_TOOLTIPS.MENTIONS_BY_LOCATION"
      :skeleton="topLocationsLoading"
      :show-see-more="seeMoreTopLocationsButtonVisible"
      :see-more-button-text="seeMoreTopLocationsButtonText"
      @see-more="handleSeeMoreTopLocations"
    >
      <template #filters="{ disabled }">
        <div class="flex gap-2">
          <slot name="topicChangeButton"></slot>
          <Select
            v-slot="{ selectionLabel }"
            v-model="topLocationsSelectedMetric"
            :disabled="disabled"
            :options="Object.values(TOP_LOCATIONS_METRIC_OPTIONS)"
            :dropdown="{ placement: 'bottom-end' }"
            :hide-controls="false"
            return-object
            option-label="optionLabel"
            @update:model-value="updateTopLocations"
          >
            <Button :label="selectionLabel" severity="tertiary" icon-right="filter-lines" />
          </Select>
        </div>
      </template>

      <HorizontalBarChart
        :loading="topLocationsLoading"
        :meters="topLocationsSlicedMeters"
        :no-data="topLocationsNoData"
        :no-data-message="noDataMessage"
        :max="topLocationsMaxValue"
        show-values
        data-test="top-locations-bar-chart"
        @meter-group-clicked="handleLocationsMeterGroupClicked"
      >
        <template #tooltip="{ context, meter }">
          <span class="typo-base-semibold">{{ meter.label }}</span>
          <MetricList :metric-items="[{ label: 'Mentions', value: formatNumber(context.value) }]" />
          <span class="typo-base text-subtle">Click a location to view posts</span>
        </template>
      </HorizontalBarChart>
    </ChartCard>
  </Section>
</template>
